import React from 'react';
import moment from 'moment';
import { IDealerTicket } from 'thunks/RTKQuery/dealer';
import i18n from 'translations';
import { WARNING_STATUS } from 'constants/constants';
import { Col, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import { Link, useHistory } from 'react-router-dom';
import exclamationTriangle from '../../../assets/images/exclamation-triangle.svg';
import { Actions } from './Actions';

interface IHideColumn {
  [key: string]: boolean;
}

const fmCustomerColumnFormatter = (
  row: IDealerTicket,
  showFMCustomer: boolean,
  openFMCustomerDialog: (row: IDealerTicket) => void
) => {
  return (
    <>
      <Row>
        <Col md={12}>
          {showFMCustomer ? (
            <Link
              to="#"
              onClick={() => {
                if (openFMCustomerDialog) {
                  openFMCustomerDialog(row);
                }
              }}
            >
              {row.fmCustomerId ? (
                `${row.fmCustomerId}`
              ) : (
                <span className="text-info">
                  {i18n.t(
                    'dealerManagement.placeholders.fmCustomerPlaceholder'
                  )}
                </span>
              )}
            </Link>
          ) : (
            <span>{row.fmCustomerId ? `${row.fmCustomerId}` : ''}</span>
          )}
        </Col>
      </Row>
    </>
  );
};

const fmCustomerDescColumnFormatter = (
  row: IDealerTicket,
  showFMCustomer: boolean,
  openFMCustomerDialog: (row: IDealerTicket) => void
) => {
  return (
    <>
      <Row>
        <Col md={12}>
          <OverlayTrigger
            placement="auto"
            delay={{ show: 100, hide: 100 }}
            overlay={(props) => (
              <Tooltip id={`customer-tooltip-${row.fmCustomerId}`} {...props}>
                {row.fmCustomerId && row.fmCustomerDescription
                  ? `${row.fmCustomerDescription}`
                  : i18n.t(
                      'dealerManagement.placeholders.fmCustomerDescriptionPlaceholder'
                    )}
              </Tooltip>
            )}
          >
            {showFMCustomer ? (
              <Link
                to="#"
                onClick={() => {
                  if (openFMCustomerDialog) {
                    openFMCustomerDialog(row);
                  }
                }}
              >
                <span className="elipsis">
                  {row.fmCustomerDescription ?? ''}
                </span>
              </Link>
            ) : (
              <span className="elipsis">
                {row.fmCustomerDescription
                  ? `${row.fmCustomerDescription}`
                  : ''}
              </span>
            )}
          </OverlayTrigger>
        </Col>
      </Row>
    </>
  );
};

const dealerColumnFormatter = (
  row: IDealerTicket,
  openDealerDialog: (row: IDealerTicket) => void
) => {
  return (
    <>
      <Row>
        <Col md={12} className="text-left">
          <OverlayTrigger
            placement="auto"
            delay={{ show: 100, hide: 100 }}
            overlay={(props) => (
              <Tooltip id={`dealer-tooltip-${row.dealerId}`} {...props}>
                {row.dealerId
                  ? `${row.dealerId} (${row.dealerName})`
                  : i18n.t('dealerManagement.placeholders.dealerPlaceholder')}
              </Tooltip>
            )}
          >
            <Link
              to="#"
              onClick={() => {
                if (openDealerDialog) {
                  openDealerDialog(row);
                }
              }}
            >
              {row.dealerId ? (
                `${row.dealerId} (${row.dealerName})`
              ) : (
                <span className="text-info">
                  {i18n.t('dealerManagement.placeholders.dealerPlaceholder')}
                </span>
              )}
            </Link>
          </OverlayTrigger>
        </Col>
      </Row>
    </>
  );
};

// hideColumns is used to hide columns based on the user settings
export const getColumns = (
  openCustomerDialog: (row: IDealerTicket) => void,
  hideColumns: IHideColumn,
  openDealerDialog: (row: IDealerTicket) => void,
  openFMCustomerDialog: (row: IDealerTicket) => void,
  handleDelete: (ticket: IDealerTicket) => void,
  showFMCustomer: boolean
) => {
  const history = useHistory();
  return [
    {
      dataField: 'ticketId',
      text: i18n.t('dealerManagement.tickets.dataColumns.ticketId'),
      sort: true,
    },
    {
      dataField: 'parentDealerId',
      text: i18n.t('dealerManagement.tickets.dataColumns.parentDealerId'),
      sort: true,
      hidden: !!hideColumns.parentDealerId,
    },
    {
      dataField: 'dealerId',
      text: i18n.t('dealerManagement.tickets.dataColumns.dealerId'),
      sort: true,
      style: { width: '150px' },
      formatter: (cell: any, row: IDealerTicket) => {
        return dealerColumnFormatter(row, openDealerDialog);
      },
    },
    {
      dataField: 'fmCustomerId',
      isDummyField: true,
      text: i18n.t('customerManagement.fmCustomerId'),
      style: { width: '140px' },
      formatter: (cell: any, row: IDealerTicket) => {
        return fmCustomerColumnFormatter(
          row,
          showFMCustomer,
          openFMCustomerDialog
        );
      },
      sort: true,
    },
    {
      dataField: 'fmCustomerDescription',
      isDummyField: true,
      text: i18n.t('customerManagement.fmDescription'),
      style: { width: '160px' },
      formatter: (cell: any, row: IDealerTicket) => {
        return fmCustomerDescColumnFormatter(
          row,
          showFMCustomer,
          openFMCustomerDialog
        );
      },
    },
    {
      dataField: 'productText',
      text: i18n.t('dealerManagement.tickets.dataColumns.productText'),
      sort: true,
    },

    {
      dataField: 'expiryDate',
      text: i18n.t('dealerManagement.tickets.dataColumns.expiryDate'),
      formatter: (cell: any, row: IDealerTicket) => {
        const d = moment(row.expiryDate);
        if (!d.isValid()) return row.expiryDate;
        return d.utc().format(i18n.t('formats.date.short'));
      },
      sort: true,
    },
    {
      dataField: 'minimumExpiryDate',
      text: i18n.t('dealerManagement.tickets.dataColumns.minimumExpiryDate'),
      formatter: (cell: any, row: IDealerTicket) => {
        const d = moment(row.minimumExpiryDate);
        if (!d.isValid()) return row.minimumExpiryDate;
        return d.utc().format(i18n.t('formats.date.short'));
      },
      sort: true,
    },
    {
      dataField: 'maintenanceExpiry',
      text: i18n.t('dealerManagement.tickets.dataColumns.maintenanceExpiry'),
      formatter: (cell: any, row: IDealerTicket) => {
        const d = moment(row.maintenanceExpiry);
        if (!d.isValid()) return row.maintenanceExpiry;
        return d.utc().format(i18n.t('formats.date.short'));
      },
      sort: true,
    },
    {
      dataField: 'isWibuUpdateAvailable',
      text: i18n.t(
        'dealerManagement.tickets.dataColumns.isWibuUpdateAvailable'
      ),
      sort: true,
      hidden: !!hideColumns.isWibuUpdateAvailable,
    },
    {
      dataField: 'isExpired',
      isDummyField: true,
      text: i18n.t('dealerManagement.tickets.dataColumns.expiryInDays'),
      formatter: (cell: any, row: IDealerTicket) => {
        const { diffDays, status } = row;
        if (status === WARNING_STATUS.INVALID) {
          return <span> -- </span>;
        }
        return (
          <span className={`text-${status}`}>
            {status !== WARNING_STATUS.DANGER ? diffDays : 'EXPIRED'}
            {status !== WARNING_STATUS.SUCCESS && (
              <svg className={`svg-triangle svg-${status}`}>
                <use xlinkHref={exclamationTriangle} />
              </svg>
            )}
          </span>
        );
      },
      sort: true,
    },
    {
      dataField: 'licenseKey',
      text: i18n.t('dealerManagement.tickets.dataColumns.ticketInfo'),
      isDummyField: true,
      hidden: !!hideColumns.licenseKey,
      formatter: (cell: any, row: IDealerTicket) => {
        return (
          <Link
            to="#"
            onClick={(e) => {
              e.preventDefault();
              history.push(`/dealer/license/info?licenseKey=${row.ticketId}`);
            }}
          >
            {i18n.t('account.company.information')}
          </Link>
        );
      },
    },

    {
      dataField: 'Customer',
      isDummyField: true,
      text: i18n.t('customerManagement.customer'),
      style: { width: '150px' },
      formatter: (cell: any, row: IDealerTicket) => {
        return (
          <>
            <Row>
              <Col md={12}>
                <OverlayTrigger
                  placement="auto"
                  delay={{ show: 100, hide: 100 }}
                  overlay={(props) => (
                    <Tooltip id={`customer-tooltip-${row.dealerId}`} {...props}>
                      {row.customer && row.customer.id
                        ? `${row.customer.company} (${row.customer.lastName})`
                        : i18n.t(
                            'dealerManagement.placeholders.customerPlaceholder'
                          )}
                    </Tooltip>
                  )}
                >
                  <Link
                    to="#"
                    onClick={() => {
                      if (openCustomerDialog) {
                        openCustomerDialog(row);
                      }
                    }}
                  >
                    {row.customer && row.customer.id ? (
                      `${row.customer.company} (${row.customer.lastName})`
                    ) : (
                      <span className="text-info">
                        {i18n.t(
                          'dealerManagement.placeholders.customerPlaceholder'
                        )}
                      </span>
                    )}
                  </Link>
                </OverlayTrigger>
              </Col>
            </Row>
          </>
        );
      },
      sort: false,
    },
    {
      dataField: 'actions',
      text: i18n.t('dealerManagement.user.columns.actions'),
      isDummyField: true,
      style: { width: '100px' },
      formatter: (cell: any, row: IDealerTicket) => {
        return <Actions ticket={row} showActions onDelete={handleDelete} />;
      },
    },
  ];
};
